import {Concept} from 'domainTypes';
import {useNavigate} from 'react-router';
import type {FullManifestation} from 'services/metadata/types';
import {useLazyGetAgentQuery} from 'services/agent';
import {
  useLazyGetManifestationByEanQuery,
  useLazyGetManifestationByIsbnQuery,
  useLazyGetManifestationQuery,
  useLazyGetWorkQuery,
} from 'services/metadata';
import {useLazyGetPrizeQuery} from 'services/prizes';
import {
  type GlobalSearchQuery,
  parseGlobalSearchQuery,
} from '../functions/parseGlobalSearchQuery';

export type LookupError = 'notFound' | 'invalidQuery';

export type LookupResult =
  | {
      concept: Concept.manifestation;
      manifestation: Pick<FullManifestation, 'id' | 'workId'>;
    }
  | {
      concept: Concept.work | Concept.agent | Concept.prize;
      id: string; // uuid
    };

const conceptPaths = {
  work: 'metadata',
  agent: 'agent',
  prize: 'prize',
} as const;

export type GlobalLookup = {
  lookup: (query: string) => Promise<LookupResult | LookupError> | LookupError;
  navigateToResult: (result: LookupResult) => void;
};

export const useGlobalSearch = (): GlobalLookup => {
  const navigate = useNavigate();

  const [getManifestationByEanQuery] = useLazyGetManifestationByEanQuery();
  const [getManifestationByIsbnQuery] = useLazyGetManifestationByIsbnQuery();
  const [getManifestationQuery] = useLazyGetManifestationQuery();
  const [getWorkQuery] = useLazyGetWorkQuery();
  const [getAgentQuery] = useLazyGetAgentQuery();
  const [getPrizeQuery] = useLazyGetPrizeQuery();

  const navigateToResult = (result: LookupResult) => {
    if (result.concept === Concept.manifestation) {
      const {workId, id} = result.manifestation;
      navigate(`/metadata/${workId}?manifestationId=${id}`);
    } else {
      navigate(`/${conceptPaths[result.concept]}/${result.id}`);
    }
  };

  const searchByIsbn = async (
    isbn: string,
  ): Promise<LookupResult | LookupError> => {
    const manifestation =
      (await getManifestationByIsbnQuery(isbn)
        .unwrap()
        .catch(() => undefined)) ??
      (await getManifestationByEanQuery(isbn)
        .unwrap()
        .catch(() => undefined));

    return manifestation
      ? {concept: Concept.manifestation, manifestation}
      : 'notFound';
  };

  const searchByConceptId = async (
    concept: Concept.work | Concept.agent | Concept.prize,
    id: string,
  ): Promise<LookupResult | LookupError> => {
    const query =
      concept === Concept.work
        ? getWorkQuery({id})
        : concept === Concept.agent
          ? getAgentQuery({id})
          : getPrizeQuery(id);
    const found = await query.unwrap().catch(() => undefined);
    return found ? {concept, id} : 'notFound';
  };

  const searchByUuid = async (
    id: string,
  ): Promise<LookupResult | LookupError> => {
    const manifestation = await getManifestationQuery(id)
      .unwrap()
      .catch(() => undefined);
    if (manifestation) {
      return {concept: Concept.manifestation, manifestation};
    }

    const work = await searchByConceptId(Concept.work, id);
    if (work !== 'notFound') {
      return work;
    }

    const agent = await searchByConceptId(Concept.agent, id);
    if (agent !== 'notFound') {
      return agent;
    }

    return searchByConceptId(Concept.prize, id);
  };

  const searchByQuery = (
    query: GlobalSearchQuery,
  ): Promise<LookupResult | LookupError> => {
    switch (query.type) {
      case 'isbn':
        return searchByIsbn(query.value);
      case Concept.work:
      case Concept.agent:
        return searchByConceptId(query.type, query.value);
      case 'uuid':
        return searchByUuid(query.value);
    }
  };

  const search = (
    query: string,
  ): Promise<LookupResult | LookupError> | LookupError => {
    const parsed = parseGlobalSearchQuery(query);
    if (!parsed) {
      return 'invalidQuery';
    }

    return searchByQuery(parsed);
  };

  return {
    lookup: search,
    navigateToResult,
  };
};
